import { Button } from '@/common/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import { Input } from '@/common/ui/input'
import { useState } from 'react'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import { useQueryClient } from '@tanstack/react-query'
import { PlayerQueryKeys } from '@/modules/manage-player/keys'
import useMutationClearPlayerTurnover from '@/modules/manage-player/api/hooks/useMutationClearPlayerTurnover'

interface PlayerClearTurnoverModalProps {
	player: {
		phone: string
		username?: string
		id: string
		promotionWallet: {
			balance: number
			turnover: number
		}
	}
}

export function PlayerClearTurnoverModal({
	player,
}: PlayerClearTurnoverModalProps) {
	const [open, setOpen] = useState(false)
	const [confirmText, setConfirmText] = useState('')
	const [note, setNote] = useState('')
	const isConfirmTextValid = confirmText === 'ยืนยัน'

	const { mutate: clearTurnover } = useMutationClearPlayerTurnover()
	const queryClient = useQueryClient()

	const handleConfirm = () => {
		if (!isConfirmTextValid) return

		try {
			clearTurnover(
				{
					playerId: player.id,
					...(note.trim() && { note: note.trim() }),
				},
				{
					onSuccess: () => {
						toast.success('ล้างเทิร์นเรียบร้อยแล้ว')
						queryClient.invalidateQueries({
							queryKey: [PlayerQueryKeys.GetPlayerDetail, player.id],
						})
						queryClient.invalidateQueries({
							queryKey: [PlayerQueryKeys.GetPlayerList],
						})
						setConfirmText('')
						setNote('')
						setOpen(false)
					},
					onError: (error) => {
						showErrorToast(error)
					},
				},
			)
		} catch (error) {
			showErrorToast(error as Error)
		}
	}

	return (
		<>
			<Button variant="default" size="sm" onClick={() => setOpen(true)}>
				ล้างเทิร์น
			</Button>
			{open && (
				<Dialog open onOpenChange={setOpen}>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>ล้างเทิร์นเงินติดเงื่อนไข</DialogTitle>
							<DialogDescription>
								เงินของผู้เล่นจะเหลือเงิน 0 และยกเลิกเงื่อนไข promotion
							</DialogDescription>
						</DialogHeader>

						<div className="space-y-4 py-4">
							<div className="space-y-2">
								<p className="text-sm font-medium">ผู้เล่น</p>
								<p className="text-sm text-muted-foreground">
									{player.username || player.phone}
								</p>
							</div>
							<div className="space-y-2">
								<p className="text-sm font-medium">Player ID</p>
								<p className="text-sm text-muted-foreground">{player.id}</p>
							</div>
							<div className="space-y-2">
								<p className="text-sm font-medium">เงินติดเงื่อนไข</p>
								<p className="text-sm text-muted-foreground">
									{player.promotionWallet?.balance || 0} บาท
								</p>
							</div>
							<div className="space-y-2">
								<p className="text-sm font-medium">ยอดเทิร์น</p>
								<p className="text-sm text-muted-foreground">
									{player.promotionWallet?.turnover || 0} บาท
								</p>
							</div>
							<div className="space-y-2">
								<p className="text-sm font-medium">
									หมายเหตุ{' '}
									<span className="font-normal text-muted-foreground">
										(ไม่บังคับ)
									</span>
								</p>
								<Input
									value={note}
									onChange={(e) => setNote(e.target.value)}
									placeholder="ระบุเหตุผลหรือหมายเหตุ"
									maxLength={500}
								/>
							</div>
							<div className="space-y-2">
								<p className="text-sm font-medium">
									กรุณาพิมพ์ &quot;ยืนยัน&quot; เพื่อดำเนินการ
								</p>
								<Input
									value={confirmText}
									onChange={(e) => setConfirmText(e.target.value)}
									placeholder="ยืนยัน"
								/>
							</div>
						</div>

						<DialogFooter>
							<Button variant="outline" onClick={() => setOpen(false)}>
								ยกเลิก
							</Button>
							<Button
								variant="destructive"
								onClick={handleConfirm}
								disabled={!isConfirmTextValid}
							>
								ยืนยัน
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			)}
		</>
	)
}
